/**
 * Las preguntas del diagnóstico, tal cual el cuaderno de WealthX (págs. 4 y 5) y la pestaña '0 · Diagnóstico' del
 * MODELO NATION v1.0. Si cambia el wording, cambia aquí y en src/ai/knowledge/mapa-pantallas.md.
 */
import type { IdpStep, WheelKey } from "@/engine/types";

export interface PasoIdp { k: IdpStep["key"]; titulo: string; preguntas: string[] }
export interface AreaRueda { k: WheelKey; titulo: string; preguntas: string[] }

/** Escala 1-5: mide madurez de diseño. */
export const PASOS_IDP: PasoIdp[] = [
  { k: "s1", titulo: "Paso 1 · Mi identidad patrimonial", preguntas: [
    "Qué tanto conozco mi historia con el dinero y las marcas que me dejó",
    "Qué tanto he hablado de dinero abiertamente con mi pareja y mi familia",
    "Qué tan consciente soy de mis creencias sobre el dinero y de cuáles me frenan",
    "Qué tanto sé qué necesidad emocional estoy tratando de llenar con el dinero",
    "Qué tan claro tengo cómo me comporto como inversionista bajo presión",
    "Qué tanto decido desde la calma y no desde el miedo o la euforia",
  ] },
  { k: "s2", titulo: "Paso 2 · Mi vida patrimonial", preguntas: [
    "Qué tan claro y por escrito tengo para qué existe mi patrimonio",
    "Qué tan definidos tengo mis valores innegociables sobre el dinero",
    "Qué tan claro tengo de qué voy a vivir en cada etapa de mi vida",
    "Qué tan definidos y con monto tengo mis objetivos patrimoniales",
    "Qué tan definido tengo el plazo de cada objetivo",
    "Qué tanto mi pareja y mi familia conocen y comparten esta visión",
  ] },
  { k: "s3", titulo: "Paso 3 · Mi realidad patrimonial", preguntas: [
    "Qué tan claro tengo cuánto tengo, cuánto debo y cuánto es mi patrimonio neto",
    "Qué tan claro tengo cuánto entra y cuánto sale cada mes",
    "Qué tanto sé qué parte de mi ingreso es pasivo y qué parte depende de mi trabajo",
    "Qué tan seguido reviso estos números (no si los tengo: si los veo)",
  ] },
  { k: "s4", titulo: "Paso 4 · Mi estrategia patrimonial", preguntas: [
    "Qué tan definidos tengo mis porcentajes objetivo entre preservar, generar y crecer",
    "Qué tanto cada inversión que tengo responde a un objetivo concreto",
    "Qué tan claras tengo mis reglas para decir sí o no a una oportunidad",
    "Qué tanto sé el rendimiento y el riesgo real de lo que tengo hoy",
  ] },
  { k: "s5", titulo: "Paso 5 · Mi sistema patrimonial", preguntas: [
    "Qué tan completo está mi equipo: fiscalista, contador, abogado, asesores",
    "Qué tan definida está la cadencia con la que reviso mi patrimonio",
    "Qué tan claro está quién decide y cómo se deciden las cosas de dinero en mi familia",
    "Qué tanto está documentado y accesible, en vez de vivir en mi cabeza",
  ] },
];

/** Escala 1-10, distinta a la del IDP a propósito: mide estado, no diseño. */
export const AREAS_RUEDA: AreaRueda[] = [
  { k: "fisica", titulo: "Riqueza Física", preguntas: [
    "Qué tanta energía tengo para sostener el ritmo de la vida que quiero",
    "Qué tan consistentes son mis hábitos de sueño, alimentación y movimiento",
    "Qué tanto cuido mi cuerpo como el activo que sostiene todo lo demás",
    "Qué tan bien manejo el estrés en mi cuerpo, no solo en mi cabeza",
  ] },
  { k: "emocional", titulo: "Riqueza Emocional", preguntas: [
    "Qué tan claro tengo mi propósito, más allá del dinero",
    "Qué tanta paz mental tengo hoy: decido desde la calma, no desde el miedo",
    "Qué tanto lo que hago día a día se parece a lo que digo que es importante",
    "Qué tanto espacio le doy a lo que me reconecta conmigo (silencio, práctica, naturaleza, fe)",
  ] },
  { k: "relaciones", titulo: "Riqueza de Relaciones", preguntas: [
    "Qué tan cerca me siento de las personas que más me importan",
    "Qué tan transparente soy con mi pareja y mi familia sobre el dinero",
    "Qué tanto tiempo de calidad (presente, sin pantalla) les doy",
    "Qué tanto mis relaciones me impulsan en vez de drenarme",
  ] },
];

export const TOTAL_IDP = PASOS_IDP.reduce((a, p) => a + p.preguntas.length, 0); // 24
export const TOTAL_RUEDA = AREAS_RUEDA.reduce((a, p) => a + p.preguntas.length, 0); // 12

export const hoyISO = () => new Date().toISOString().slice(0, 10);
const MESES_ENTRE_DIAGNOSTICOS = 3;

/** "19 oct 2026" */
export const fechaCorta = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });

/** Cuándo toca el siguiente diagnóstico (cada trimestre) y si ya se pasó. */
export function siguienteDiagnostico(answeredAt: string | null, hoy = hoyISO()): { fecha: string; vencido: boolean } | null {
  if (!answeredAt) return null;
  const d = new Date(`${answeredAt.slice(0, 10)}T12:00:00`);
  d.setMonth(d.getMonth() + MESES_ENTRE_DIAGNOSTICOS);
  const fecha = d.toISOString().slice(0, 10);
  return { fecha, vencido: fecha <= hoy };
}
